import { useEffect, useMemo, useState, type ReactNode } from "react";
import { Form, Formik, useFormikContext, type FormikHelpers } from "formik";
import * as Yup from "yup";
import ModalDialog, {
  ModalDialogBody,
  ModalDialogFooter,
} from "../UI/ModalDialog";
import Button from "../UI/Button";
import FormikSubmitButton from "../UI/FormikSubmitButton";
import Text from "../UI/Text";
import Callout from "../UI/Callout";
// @ts-expect-error pure js
import Checkbox from "../UI/Checkbox";
// @ts-expect-error pure js
import TextInput from "../UI/TextInput";
// @ts-expect-error pure js
import { handleAPIError } from "../../utils";
// @ts-expect-error pure js
import useTenants from "../../hooks/use-tenants";
// @ts-expect-error pure js
import useDebouncedValue from "../../hooks/use-debounced-value";
import { useToast } from "../UI/Toast/useToast";
import BulkPdfPreview from "./Bulk/BulkPdfPreview";
import { usePdfPreviewCache } from "./Bulk/usePdfPreviewCache";
import bulkStyles from "./Bulk/BulkActions.module.css";
import styles from "./AttestationCafModal.module.css";
import type { CafDirectPayment } from "../../models/cafDirectPayment";

interface AttestationCafFormValues {
  percevoirDirect: boolean;
  isChambre: boolean;
  directPayment: CafDirectPayment;
}

interface AttestationCafPayload {
  tenantId: number;
  percevoirDirect: boolean;
  isChambre: boolean;
  directPayment: CafDirectPayment | null;
}

interface AttestationCafModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  tenantId: number;
  tenantName: string;
  isColoc: boolean;
}

const EMPTY_DIRECT_PAYMENT: CafDirectPayment = {
  iban: "",
  bic: "",
  bank_branch: "",
};

const IBAN_PATTERN = /^[A-Z]{2}\d{2}[A-Z0-9]{11,30}$/;
const BIC_PATTERN = /^[A-Z]{6}[A-Z0-9]{2}([A-Z0-9]{3})?$/;

function compact(value: string | undefined) {
  return (value ?? "").replace(/\s+/g, "").toUpperCase();
}

const validationSchema = Yup.object({
  percevoirDirect: Yup.boolean(),
  directPayment: Yup.object().when("percevoirDirect", {
    is: true,
    then: (schema) =>
      schema.shape({
        iban: Yup.string()
          .required("L'IBAN est obligatoire pour le versement direct")
          .test("iban", "IBAN invalide", (value) =>
            IBAN_PATTERN.test(compact(value)),
          ),
        bic: Yup.string().test(
          "bic",
          "BIC invalide",
          (value) => !compact(value) || BIC_PATTERN.test(compact(value)),
        ),
      }),
  }),
});

function toPayload(
  tenantId: number,
  values: AttestationCafFormValues,
): AttestationCafPayload {
  return {
    tenantId,
    percevoirDirect: values.percevoirDirect,
    isChambre: values.isChambre,
    directPayment: values.percevoirDirect
      ? {
          ...values.directPayment,
          iban: compact(values.directPayment.iban),
          bic: compact(values.directPayment.bic),
        }
      : null,
  };
}

export default function AttestationCafModal({
  open,
  onOpenChange,
  tenantId,
  tenantName,
  isColoc,
}: AttestationCafModalProps) {
  const {
    getAttestationCafDirectPayment,
    createAttestationCaf,
    previewAttestationCafPdf,
  }: {
    getAttestationCafDirectPayment: (
      tenantId: number,
    ) => Promise<CafDirectPayment>;
    createAttestationCaf: (payload: AttestationCafPayload) => Promise<unknown>;
    previewAttestationCafPdf: (
      payload: AttestationCafPayload,
    ) => Promise<string>;
  } = useTenants();
  const toast = useToast();
  const [defaultDirectPayment, setDefaultDirectPayment] =
    useState<CafDirectPayment>(EMPTY_DIRECT_PAYMENT);
  const [directPaymentError, setDirectPaymentError] = useState<ReactNode>();

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setDirectPaymentError(undefined);
    getAttestationCafDirectPayment(tenantId)
      .then((directPayment) => {
        if (!cancelled) setDefaultDirectPayment(directPayment);
      })
      .catch((e) => {
        if (!cancelled) setDirectPaymentError(handleAPIError(e));
      });
    return () => {
      cancelled = true;
    };
  }, [open, tenantId, getAttestationCafDirectPayment]);

  async function handleSubmit(
    values: AttestationCafFormValues,
    { setSubmitting, setStatus }: FormikHelpers<AttestationCafFormValues>,
  ) {
    setStatus(undefined);
    try {
      await createAttestationCaf(toPayload(tenantId, values));
      toast.success({
        title: "Attestation de loyer CAF générée",
        description: `Le document a été ajouté aux documents de ${tenantName} et envoyé par mail au locataire.`,
      });
      onOpenChange(false);
    } catch (e) {
      setStatus(handleAPIError(e));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Formik<AttestationCafFormValues>
      initialValues={{
        percevoirDirect: false,
        isChambre: isColoc,
        directPayment: defaultDirectPayment,
      }}
      enableReinitialize
      validationSchema={validationSchema}
      onSubmit={handleSubmit}
    >
      {({ resetForm }) => (
        <ModalDialog
          open={open}
          onOpenChange={(isOpen) => {
            if (!isOpen) resetForm();
            onOpenChange(isOpen);
          }}
          size="xl"
          header="Attestation de loyer CAF"
          subtitle={`Formulaire Cerfa 10842*07 « Attestation de loyer » pré-rempli pour ${tenantName}`}
        >
          <Form>
            <AttestationCafFields
              open={open}
              tenantId={tenantId}
              directPaymentError={directPaymentError}
              previewAttestationCafPdf={previewAttestationCafPdf}
              close={() => {
                resetForm();
                onOpenChange(false);
              }}
            />
          </Form>
        </ModalDialog>
      )}
    </Formik>
  );
}

interface AttestationCafFieldsProps {
  open: boolean;
  tenantId: number;
  directPaymentError: ReactNode;
  previewAttestationCafPdf: (payload: AttestationCafPayload) => Promise<string>;
  close: () => void;
}

function AttestationCafFields({
  open,
  tenantId,
  directPaymentError,
  previewAttestationCafPdf,
  close,
}: AttestationCafFieldsProps) {
  const { values, status } = useFormikContext<AttestationCafFormValues>();

  // Les champs du versement direct se tapent au clavier : l'aperçu attend une pause de saisie
  const previewKey: string = useDebouncedValue(
    JSON.stringify(toPayload(tenantId, values)),
    700,
  );
  const previewPayload = useMemo(
    () => JSON.parse(previewKey) as AttestationCafPayload,
    [previewKey],
  );

  const { previewUrl, previewLoading, previewError } = usePdfPreviewCache({
    activeKey: open ? previewKey : null,
    activePayload: previewPayload,
    fetcher: previewAttestationCafPdf,
    enabled: open,
  });

  return (
    <>
      <ModalDialogBody className={bulkStyles.body}>
        <div className={bulkStyles.split}>
          <div className={styles.options}>
            <Text>
              Le formulaire est pré-rempli à partir des informations du bail, du
              logement et du bailleur. Vérifiez l'aperçu avant l'envoi.
            </Text>
            <Checkbox name="isChambre">
              Le logement loué est une chambre (pièce unique sans WC)
            </Checkbox>
            <Checkbox name="percevoirDirect">
              Percevoir directement l'aide au logement (versement de la CAF au
              bailleur)
            </Checkbox>
            {values.percevoirDirect && (
              <div className={styles.directPayment}>
                <Text>
                  La demande de versement direct (page 2 du Cerfa) est remplie
                  avec le nom et l'adresse du bailleur et le compte ci-dessous,
                  pré-rempli depuis votre profil.
                </Text>
                <Callout
                  variant="warning"
                  title="Impossible de pré-remplir les coordonnées bancaires"
                >
                  {directPaymentError}
                </Callout>
                <div className={styles.bankRow}>
                  <TextInput
                    name="directPayment.iban"
                    label="IBAN"
                    mandatory
                    marginForm=""
                  />
                  <TextInput
                    name="directPayment.bic"
                    label="BIC"
                    marginForm=""
                  />
                </div>
                <TextInput
                  name="directPayment.bank_branch"
                  label="Domiciliation de l'agence bancaire"
                  placeholder="Ex. : Crédit Agricole, agence de Nancy"
                  marginForm=""
                />
              </div>
            )}
          </div>

          <BulkPdfPreview
            iframeTitle="Aperçu attestation de loyer CAF"
            hasSelection
            isIneligible={false}
            ineligibleTitle=""
            previewUrl={previewUrl}
            previewLoading={previewLoading}
            previewError={previewError}
          />
        </div>

        <Callout variant="danger">{status}</Callout>
      </ModalDialogBody>
      <ModalDialogFooter
        start={
          <Text>
            Le document sera ajouté aux documents du locataire et lui sera
            envoyé par mail.
          </Text>
        }
      >
        <Button variant="secondary" type="button" onClick={close}>
          Annuler
        </Button>
        <FormikSubmitButton>Générer et envoyer au locataire</FormikSubmitButton>
      </ModalDialogFooter>
    </>
  );
}
